<template>
  <div
    class="font-sans bg-white shadow border border-solid border-white rounded-2xl flex flex-col @container"
    data-test="campaign-row">
    <div
      class="flex min-h-14 items-center px-6 border-b border-x-0 border-t-0 border-solid rounded-t-2xl relative justify-between"
      :class="[CAMPAIGN_STATUSES[props.modelValue.status]?.background]">
      <div class="flex items-center">
        <campaign-badge class="mr-4" :status="props.modelValue.status" />
        <campaign-card-actions :modelValue="props.modelValue" @update:modelValue="emit('update:modelValue', $event)"
          @launch="emit('launch', $event)">
        </campaign-card-actions>
      </div>

      <vx-popover
        v-if="FEATURES.flows && props.modelValue.flow_id"
        hover
        :arrow="true"
        placement="top"
        @mouseenter="loadFlowOnHover"
        data-test="in-flow-badge"
      >
        <div class="rounded-sm px-2 py-1 text-xs font-medium" :class="flowBadgeClasses">
          In Flow
          <font-awesome-icon widthAuto class="ml-1" :icon="faSlidersSimple"></font-awesome-icon>
        </div>
        <template v-slot:content>
          <span v-if="flow" class="text-sm font-normal">{{ flow.name }}</span>
          <font-awesome-icon v-else widthAuto :icon="faLoader" spin></font-awesome-icon>
        </template>
      </vx-popover>
    </div>

    <div
      class="flex flex-col px-6 py-6 lg:py-4 rounded-b-2xl relative gap-4 lg:grid-cols-5 lg:inline-grid h-full">
      <div class="space-y-1 col-span-3">
        <div class="grow flex items-baseline">
          <vx-badge class="mr-2 cursor-pointer" @click.prevent="copyText(props.modelValue.id)" size="xs" color="slate">{{ props.modelValue.id }}</vx-badge>

          <h3 class="lg:line-clamp-2 text-base font-bold text-slate-800 line-clamp-none break-all leading-normal m-0 cursor-pointer"
          data-test="campaign-name" @click.prevent="copyText(props.modelValue.name)">
            {{ props.modelValue.name }}
          </h3>
            <resource-details-popover
                class="ml-1"
                :modelValue="{ ...props.modelValue, resource_type: 'campaign' }"
                :teamId="props.modelValue.team_id"
            ></resource-details-popover>
        </div>

        <div class="text-slate-400 text-sm mb-4 lg:mb-0">
          <campaign-card-dates :modelValue="props.modelValue"/>
        </div>
      </div>

      <div
        class="text-slate-500 text-base flex flex-row gap-6 lg:justify-center lg:items-center">
        <vx-popover hover placement="top" arrow>
            <div class="flex items-center gap-1 lg:mb-0">
                <font-awesome-icon widthAuto class="text-xs" :icon="faUserGroup"></font-awesome-icon>
                <font-awesome-icon widthAuto v-if="contactsCount === null" class="text-xs" :icon="faSpinnerThird"
                    :spin="true"></font-awesome-icon>
                <template v-else>
                    {{ vxFormatCount(contactsCount) }}
                </template>
            </div>
            <template v-slot:content>
                <strong>Contacts</strong><br />
                Currently in campaign: &nbsp; <strong>{{ vxFormatCount(contactsCount) }}</strong>
            </template>
        </vx-popover>

        <vx-popover hover placement="top" arrow>
            <div class="flex items-center gap-1 lg:mb-0" data-test="published-messages-vs-total-messages">
                <font-awesome-icon widthAuto class="text-xs" :icon="faMessages"></font-awesome-icon>
                {{ props.modelValue.published_campaign_messages_count }} / {{ props.modelValue.total_campaign_messages_count }}
            </div>
            <template v-slot:content>
                <strong>Messages</strong><br />
                <div class="flex flex-col min-w-52">
                    <div class="flex justify-between">
                        <span>Active:</span><strong data-test="published-messages-count">{{ props.modelValue.published_campaign_messages_count }}</strong>
                    </div>
                    <div class="flex justify-between">
                        <span>Total:</span><strong data-test="total-messages-count">{{ props.modelValue.total_campaign_messages_count }}</strong>
                    </div>
                </div>
            </template>
        </vx-popover>
      </div>

      <div class="flex gap-1 items-end justify-end grow">
        <campaign-card-dropdown :campaign="props.modelValue" @deleted="emit('deleted', $event)">
        </campaign-card-dropdown>

        <router-button color="muted" :outline="true" :to="{ name: 'campaigns.edit', params: { id: props.modelValue.id } }" class="grow lg:grow-0">
          Edit
          <font-awesome-icon widthAuto :icon="faFilePen"></font-awesome-icon>
        </router-button>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  faFilePen,
  faSpinnerThird,
  faUserGroup,
  faMessages,
  faLoader
} from '@fortawesome/pro-solid-svg-icons';
import { VxBadge, vxFormatCount, VxPopover } from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { ref, inject, computed } from 'vue';
import CampaignCardDates from './CampaignCardDates.vue';
import CampaignCardActions from './CampaignCardActions.vue';
import CampaignCardDropdown from './CampaignCardDropdown.vue';
import { campaignsClient } from '~/services';
import { useResourcesFlow } from '~/composables/useResourcesFlow';
import { useCopyText } from '~/composables/useCopyText';
import ResourceDetailsPopover from '~/components/general/resources/ResourceDetailsPopover.vue';
import { CAMPAIGN_STATUSES } from '~/constants/campaign';
import CampaignBadge from './CampaignBadge.vue';
import RouterButton from '~/components/general/RouterButton.vue';
import { faSlidersSimple } from '@fortawesome/pro-regular-svg-icons';

const FEATURES = inject('FEATURES');

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['update:modelValue', 'deleted', 'launch']);

const { copyText } = useCopyText();

const contactsCount = ref(null);

const { flows, loadFlow } = useResourcesFlow();
const flow = computed(() => FEATURES.flows && props.modelValue.flow_id && flows.value.has(props.modelValue.flow_id)
    ? flows.value.get(props.modelValue.flow_id)
    : undefined
);

const flowBadgeClasses = computed(() => {
    const status = props.modelValue.status;
    const colorMap = {
        'draft': 'bg-slate-100 text-slate-900',
        'scheduled': 'bg-orange-100 text-orange-900',
        'published': 'bg-teal-100 text-teal-900',
        'completed': 'bg-slate-100 text-slate-900',
        'paused': 'bg-slate-100 text-slate-900',
        'archived': 'bg-slate-100 text-slate-900',
    };
    return colorMap[status] || 'bg-slate-100 text-slate-900';
});

campaignsClient
  .campaignsContactsCount(props.modelValue.team_id, props.modelValue.id)
  .then(({ data }) => (contactsCount.value = data.count));

const loadFlowOnHover = () => {
    if (flow.value === undefined) {
        loadFlow(props.modelValue.team_id, props.modelValue.flow_id);
    }
};
</script>
